import { Box, MenuItem, TextField } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { findFilterValueTranslationId } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';
import { useSearchParams } from 'react-router-dom';

import { PaloListFilterField, PaloListFilterOperator, paloDefaultOperator } from './use-palo-list-filters';
import { paloParseComparison, paloWriteComparison } from './palo-filter-comparison';

/**
 * How a comparison is written into the address.
 *
 * `filter[number][starts]=112`. The plain form `filter[status]=x` keeps
 * working and means the comparison the kind implies, so an address written
 * before this existed still opens (#2209).
 */
export const paloFilterParam = (key: string, operator: PaloListFilterOperator): string => `filter[${key}][${operator}]`;

/** Every way this field may stand in the address, so all of them can go. */
const paramsOf = (field: PaloListFilterField): string[] => [
    `filter[${field.key}]`,
    ...field.operators.map(operator => paloFilterParam(field.key, operator)),
];

export const paloReadFilter = (
    params: URLSearchParams,
    field: PaloListFilterField
): { operator: PaloListFilterOperator; value: string } => {
    for (const operator of field.operators) {
        const held = params.get(paloFilterParam(field.key, operator));

        if (null !== held) {
            return { operator, value: held };
        }
    }

    return { operator: paloDefaultOperator(field), value: params.get(`filter[${field.key}]`) ?? '' };
};

/**
 * What a value is called.
 *
 * The list hands out the values it accepts, not their wording — a status is
 * named in one place, and that place is the translation file.
 */
const choiceLabel = (value: string): ReactNode => {
    const id = findFilterValueTranslationId(value);

    return id ? <FormattedMessage id={id} /> : value;
};

/**
 * One declared field's filter, in the row above the table beside the
 * hand-written quick filters.
 *
 * What it offers comes from the list itself: the kind decides the control, and
 * a choice names the values it takes. Typing waits a moment before asking the
 * server — a filter that reloads on every keystroke is unusable on a list of
 * 72.000 rows.
 */
export const PaloColumnFilter = ({ field }: { field: PaloListFilterField }): ReactElement => {
    const [searchParams, setSearchParams] = useSearchParams();
    const applied = paloReadFilter(searchParams, field);
    const choices = field.choices ?? [];
    const isChoice = choices.length > 0;
    // A number takes a comparison as well as a value: `>3`, `1000-2000`.
    const takesComparison = ('number' === field.type || 'money' === field.type) && !isChoice;
    const shown =
        '' === applied.value || !takesComparison
            ? applied.value
            : paloWriteComparison(
                  { operator: applied.operator, values: applied.value.split(',') },
                  'money' === field.type
              );
    const [draft, setDraft] = useState(shown);
    const [isUnderstood, setIsUnderstood] = useState(true);

    // Someone else may change the address: a chip removed, a view applied, the
    // state restored when the list opens.
    useEffect(() => {
        setDraft(shown);
        setIsUnderstood(true);
    }, [shown]);

    const apply = (text: string): void => {
        const typed = text.trim();
        const comparison = takesComparison && '' !== typed ? paloParseComparison(typed, field) : null;

        if (takesComparison && '' !== typed && !comparison) {
            // Sent as it stands, `>3` reached the list as the value of an
            // equals, which it answered with a server error (#2231 R-117).
            // Marked here instead, where it can still be corrected.
            setIsUnderstood(false);

            return;
        }

        setIsUnderstood(true);

        const operator = comparison?.operator ?? applied.operator;
        const value = comparison ? comparison.values.join(',') : text;

        if (value === applied.value && operator === applied.operator) {
            return;
        }

        const next = new URLSearchParams(searchParams);

        paramsOf(field).forEach(param => next.delete(param));

        if ('' !== value) {
            next.set(paloFilterParam(field.key, operator), value);
        }

        // A narrowed list starts at its beginning; staying on page 7 of a
        // result that now has two pages shows nothing at all.
        next.set('page', '1');
        setSearchParams(next);
    };

    // A date gets the browser's own picker, a number its spinner-less number
    // field; everything else is a plain box.
    const type = 'date' === field.type ? 'date' : 'text';

    return (
        <TextField
            size="small"
            type={type}
            select={isChoice}
            error={!isUnderstood}
            title={isUnderstood ? undefined : 'Nicht verstanden. Möglich sind z. B. 4, >3, ≥3, <10 oder 3-5.'}
            value={isChoice ? applied.value : draft}
            // A date field has no room for a placeholder next to the picker,
            // so the column heading above it is what names it.
            onChange={event => (isChoice || 'date' === type ? apply(event.target.value) : setDraft(event.target.value))}
            onBlur={() => (isChoice ? undefined : apply(draft))}
            onKeyDown={event => {
                if ('Enter' === event.key) {
                    apply(draft);
                }
            }}
            sx={{
                width: '100%',
                '& .MuiInputBase-input': { fontSize: '12.5px', color: tokens.color.ink, py: '4px' },
            }}
        >
            <MenuItem value="">
                <Box component="span" sx={{ color: tokens.color.ink3 }}>
                    Alle
                </Box>
            </MenuItem>
            {choices.map(choice => (
                <MenuItem key={choice} value={choice}>
                    {choiceLabel(choice)}
                </MenuItem>
            ))}
        </TextField>
    );
};
